import type {
  TeamsOverviewResponse,
  TeamsStaffTopRowApi,
  TeamsTeamTopRowApi,
} from "@/types/analytics-teams";
import type { MetricCardTrend } from "../shared/MetricCard";
import type {
  TeamKpiComparisonByKey,
  TeamSegment,
  TeamSegmentData,
  TeamStaffRow,
  TeamSummary,
} from "./types";

const DELTA_SUFFIX = " from previous period";

function resolveStaffPhotoUrl(photoId: string | null): string | undefined {
  if (!photoId?.trim()) {
    return undefined;
  }

  if (/^https?:\/\//i.test(photoId)) {
    return photoId;
  }

  return undefined;
}

function buildDisplayName(
  firstName: string | null,
  lastName: string | null,
): string {
  const parts = [firstName, lastName].filter((part): part is string =>
    Boolean(part?.trim()),
  );

  return parts.length > 0 ? parts.join(" ") : "—";
}

function buildInitials(name: string): string {
  const tokens = name.split(/\s+/).filter((token) => token && token !== "—");

  if (tokens.length === 0) {
    return "?";
  }

  if (tokens.length === 1) {
    return tokens[0].slice(0, 2).toUpperCase();
  }

  return `${tokens[0][0] ?? ""}${tokens[1][0] ?? ""}`.toUpperCase();
}

export function mapOverviewToSummary(
  overview: TeamsOverviewResponse | undefined,
): TeamSummary {
  if (!overview) {
    return {
      tasksCompleted: 0,
      tasksCancelled: 0,
      avgTimeOnTask: "0s",
      delays: 0,
      daysOff: 0,
    };
  }

  return {
    tasksCompleted: overview.tasksCompleted,
    tasksCancelled: overview.tasksCanceled,
    avgTimeOnTask: overview.avgTimeOnTask.humanReadable,
    delays: overview.delays,
    daysOff: overview.dayOff,
  };
}

function percentChange(current: number, previous: number): number | null {
  if (current === previous) {
    return 0;
  }

  if (previous === 0) {
    return current === 0 ? 0 : 100;
  }

  return Number((((current - previous) / previous) * 100).toFixed(1));
}

function formatPercentDelta(changePct: number | null): string | undefined {
  if (changePct == null) {
    return undefined;
  }

  const sign = changePct > 0 ? "+" : "";
  const formatted = changePct.toLocaleString("en-US", {
    maximumFractionDigits: 1,
  });

  return `${sign}${formatted}%${DELTA_SUFFIX}`;
}

function trendFromChange(
  changePct: number | null,
  higherIsBetter: boolean,
): MetricCardTrend {
  if (changePct == null || changePct === 0) {
    return "neutral";
  }

  if (higherIsBetter) {
    return changePct > 0 ? "positive" : "negative";
  }

  return changePct < 0 ? "positive" : "negative";
}

function buildComparisonDelta(
  current: number,
  previous: number,
  higherIsBetter: boolean,
): { delta?: string; trend?: MetricCardTrend } {
  const changePct = percentChange(current, previous);

  return {
    delta: formatPercentDelta(changePct),
    trend: trendFromChange(changePct, higherIsBetter),
  };
}

export function buildTeamKpiComparison(
  current: TeamsOverviewResponse | undefined,
  previous: TeamsOverviewResponse | undefined,
  segment: TeamSegment,
): TeamKpiComparisonByKey {
  if (!current || !previous) {
    return {};
  }

  const comparison: TeamKpiComparisonByKey = {
    tasksCompleted: buildComparisonDelta(
      current.tasksCompleted,
      previous.tasksCompleted,
      true,
    ),
    tasksCancelled: buildComparisonDelta(
      current.tasksCanceled,
      previous.tasksCanceled,
      false,
    ),
    avgTimeOnTask: buildComparisonDelta(
      current.avgTimeOnTask.avgSeconds,
      previous.avgTimeOnTask.avgSeconds,
      false,
    ),
  };

  if (segment === "staff") {
    comparison.delays = buildComparisonDelta(
      current.delays,
      previous.delays,
      false,
    );
    comparison.daysOff = buildComparisonDelta(
      current.dayOff,
      previous.dayOff,
      false,
    );
  }

  return comparison;
}

function mapStaffTopRow(row: TeamsStaffTopRowApi, rank: number): TeamStaffRow {
  const name = buildDisplayName(row.user.firstName, row.user.lastName);

  return {
    rank,
    id: row.user.staffId,
    name,
    initials: buildInitials(name),
    photoUrl: resolveStaffPhotoUrl(row.user.photoId),
    role: row.user.position?.trim() || "Staff",
    tasksCompleted: row.completed,
    tasksCancelled: row.canceled,
    avgTimeOnTask: row.avgTimeHumanReadable,
    delays: row.delays,
    daysOff: row.dayOff,
  };
}

function mapTeamTopRow(row: TeamsTeamTopRowApi, rank: number): TeamStaffRow {
  const name = row.team.name?.trim() || "—";

  return {
    rank,
    id: row.team.teamId,
    name,
    initials: buildInitials(name),
    role: row.team.isActive ? "Team" : "Inactive team",
    tasksCompleted: row.completed,
    tasksCancelled: row.canceled,
    avgTimeOnTask: row.avgTimeHumanReadable,
    delays: 0,
    daysOff: 0,
  };
}

export function mapStaffTopRows(
  rows: TeamsStaffTopRowApi[] | undefined,
): TeamStaffRow[] {
  return (rows ?? []).map((row, index) => mapStaffTopRow(row, index + 1));
}

export function mapTeamTopRows(
  rows: TeamsTeamTopRowApi[] | undefined,
): TeamStaffRow[] {
  return (rows ?? []).map((row, index) => mapTeamTopRow(row, index + 1));
}

export function buildTopThree(rows: TeamStaffRow[]): TeamStaffRow[] {
  return rows.slice(0, 3).map((row, index) => ({
    ...row,
    rank: index + 1,
  }));
}

export function mapTeamsSegmentData(
  overview: TeamsOverviewResponse | undefined,
  staffRows: TeamStaffRow[],
  teamRows: TeamStaffRow[],
  segment: "staff" | "teams",
): TeamSegmentData {
  const tableRows = segment === "teams" ? teamRows : staffRows;

  return {
    summary: mapOverviewToSummary(overview),
    topThree: buildTopThree(tableRows),
    staffRows: tableRows.slice(3),
  };
}

export function findTeamRowById(
  topThree: TeamStaffRow[],
  tableRows: TeamStaffRow[],
  id: string,
): TeamStaffRow | undefined {
  return [...topThree, ...tableRows].find((row) => row.id === id);
}
